import {
  AI_NOTEBOOK_ALLOWED_MIME_TYPES_LIST,
  AI_NOTEBOOK_MAX_FILE_SIZE_BYTES,
  AI_NOTEBOOK_MAX_PROMPT_LENGTH,
  AI_NOTEBOOK_MAX_TOTAL_LESSON_PLAN_CHARS,
  AI_NOTEBOOK_MAX_UPLOAD_FILES,
} from '@lib-server/services/aiNotebookConstants';
import { z } from 'zod';

const trimToUndefined = (value: unknown) => {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const optionalTrimmedString = (maxLength: number) =>
  z.preprocess(trimToUndefined, z.string().trim().max(maxLength).optional());

const requiredTrimmedString = ({
  maxLength,
  message,
}: {
  maxLength: number;
  message: string;
}) =>
  z.preprocess(
    trimToUndefined,
    z.string().trim().min(1, message).max(maxLength)
  );

const aiNotebookMimeTypeSchema = z.enum(AI_NOTEBOOK_ALLOWED_MIME_TYPES_LIST);

export const aiNotebookUploadedFileSchema = z
  .object({
    bucket: optionalTrimmedString(120).nullable().optional(),
    fileKey: requiredTrimmedString({
      maxLength: 512,
      message: 'File key is required.',
    }),
    fileName: optionalTrimmedString(255),
    fileType: aiNotebookMimeTypeSchema,
  })
  .strict();

export const aiNotebookGenerateSchema = z
  .object({
    bucket: optionalTrimmedString(120).nullable().optional(),
    fileKey: optionalTrimmedString(512),
    fileName: optionalTrimmedString(255),
    fileType: aiNotebookMimeTypeSchema.optional(),
    files: z
      .array(aiNotebookUploadedFileSchema)
      .max(AI_NOTEBOOK_MAX_UPLOAD_FILES)
      .optional(),
    grade: optionalTrimmedString(80),
    grades: z.array(z.string().trim().min(1).max(80)).optional(),
    lessonPlanText: optionalTrimmedString(
      AI_NOTEBOOK_MAX_TOTAL_LESSON_PLAN_CHARS
    ),
    notebookId: optionalTrimmedString(64),
    notebookName: optionalTrimmedString(80),
    prompt: optionalTrimmedString(AI_NOTEBOOK_MAX_PROMPT_LENGTH),
    unit: optionalTrimmedString(80),
  })
  .strict()
  .superRefine((value, ctx) => {
    const hasSingleFile = Boolean(value.fileKey);
    const hasMultiFile = Array.isArray(value.files) && value.files.length > 0;
    const hasInlineText = Boolean(value.lessonPlanText);
    const hasPrompt = Boolean(value.prompt);

    if (!hasSingleFile && !hasMultiFile && !hasInlineText && !hasPrompt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Add lesson plan files, pasted notes, or AI guidance before generating.',
      });
    }

    if (hasSingleFile && !value.fileType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'File type is required when a file key is provided.',
        path: ['fileType'],
      });
    }
  });

export const aiNotebookFeedbackSchema = z
  .object({
    baseTemplateId: requiredTrimmedString({
      maxLength: 64,
      message: 'Base template id is required.',
    }),
    displayName: requiredTrimmedString({
      maxLength: 80,
      message: 'Display name is required.',
    }),
    fieldEditsCount: z.number().int().min(0).optional(),
    freeform: optionalTrimmedString(1000).nullable().optional(),
    rating: z.number().min(1).max(5).optional(),
    runId: z.preprocess(
      trimToUndefined,
      z.string().trim().uuid('Run id is required.')
    ),
    templateId: optionalTrimmedString(64),
    textLabels: z
      .array(
        z
          .object({
            key: requiredTrimmedString({
              maxLength: 120,
              message: 'Text label key is required.',
            }),
            text: z.preprocess(
              (value) => (typeof value === 'string' ? value.trim() : value),
              z.string().max(AI_NOTEBOOK_MAX_TOTAL_LESSON_PLAN_CHARS)
            ),
          })
          .strict()
      )
      .min(1, 'Text labels are required.'),
  })
  .strict();

export const aiNotebookUploadUrlSchema = z
  .object({
    fileName: requiredTrimmedString({
      maxLength: 255,
      message: 'File name is required.',
    }),
    fileSize: z
      .number()
      .int()
      .positive('File size is required.')
      .max(
        AI_NOTEBOOK_MAX_FILE_SIZE_BYTES,
        `File exceeds ${AI_NOTEBOOK_MAX_FILE_SIZE_BYTES} byte limit`
      ),
    fileType: aiNotebookMimeTypeSchema,
    notebookId: optionalTrimmedString(64),
  })
  .strict();
